import { useEffect, useRef } from 'react';

interface Section {
    id: string;
    title: string;
}

export function useTabsAutoScroll(
    isLoading: boolean,
    sections: Section[],
    tabLinkClass: string,
) {
    const initialHashRef = useRef(window.location.hash.replace('#', '').toLowerCase());
    const hasAutoScrolledRef = useRef(false);

    useEffect(() => {
        if (isLoading || hasAutoScrolledRef.current || !initialHashRef.current) return;

        const target = sections.find(s => s.id.toLowerCase() === initialHashRef.current);
        if (!target) return;

        const activeClass = `${tabLinkClass}--active`;
        let userInteracted = false;
        let isProgrammaticClick = false;
        let initialTimer: number | undefined;
        let intervalId: number | undefined;
        let stopTimer: number | undefined;

        const stop = () => {
            window.clearTimeout(initialTimer);
            window.clearInterval(intervalId);
            window.clearTimeout(stopTimer);
        };

        const handleUserTabClick = () => {
            if (isProgrammaticClick) return;
            userInteracted = true;
            stop();
        };

        const getTargetTab = () =>
            Array.from(document.querySelectorAll(`.${tabLinkClass}`)).find(
                el => el.textContent?.trim().toLowerCase() === target.title.toLowerCase()
            ) as HTMLElement | undefined;

        const tryScroll = () => {
            if (userInteracted || hasAutoScrolledRef.current) return;
            const tab = getTargetTab();
            if (!tab || tab.classList.contains(activeClass)) return;
            isProgrammaticClick = true;
            tab.click();
            isProgrammaticClick = false;
            hasAutoScrolledRef.current = true;
            stop();
        };

        document.querySelectorAll(`.${tabLinkClass}`).forEach(
            tab => tab.addEventListener('click', handleUserTabClick)
        );

        initialTimer = window.setTimeout(() => {
            intervalId = window.setInterval(tryScroll, 500);
            stopTimer = window.setTimeout(stop, 9000);
            tryScroll();
        }, 1000);

        return () => {
            stop();
            document.querySelectorAll(`.${tabLinkClass}`).forEach(
                tab => tab.removeEventListener('click', handleUserTabClick)
            );
        };
    }, [isLoading, sections, tabLinkClass]);
}
